import './style.css';
import { AudioDirector, type VoiceCue } from './audio';
import { DIFFICULTIES, difficultyById } from './characters';
import { loadProfile, mastery, recordMatch, recordPuzzle, roleRecord, saveProfile, type MatchOutcome, type PlayerProfile } from './profile';
import { BOARD_SIZE, boardFromMoves, isBoardFull, nextSide, notation, winningLine } from './rules';
import { RapfiClient, type EngineUpdate } from './rapfi';
import { explainStyledMove, type MoveInsight } from './style';
import { dailyPuzzle, isPuzzleSolution, localDateKey, TRAINING_PUZZLES, type TrainingPuzzle } from './training';
import type { Difficulty, Move, PlayerSide, Rule, SavedGame } from './types';
import { VOICE_CUE_LABEL, VOICE_CUES, VOICE_LINES } from './voice-lines';
import { VOICE_CAST, VOICE_CAST_VERSION } from './voice-cast';

const RULE_LABEL: Record<Rule, string> = { freestyle: '自由規則', standard: '標準規則', renju: '連珠規則' };
const STORAGE_KEY = 'cloud-gomoku-guardians-v1';

let difficulty: Difficulty = DIFFICULTIES[1];
let rule: Rule = 'freestyle';
let humanSide: PlayerSide = 1;
let moves: Move[] = [];
let forbidden = new Set<string>();
let forbiddenUnavailable = false;
let winning = new Set<string>();
let thinking = false;
let gameOver = false;
let moveInsight: MoveInsight | null = null;
let profile = loadProfile();
let profileBeforeResult: PlayerProfile | null = null;
let replayMode = false;
let replayMoves: Move[] = [];
let replayIndex = 0;
let selectedPuzzle: TrainingPuzzle = dailyPuzzle();
let trainingSolved = false;
let trainingMessage = '';
let engineState: EngineUpdate = { status: 'loading', message: '正在載入 Rapfi' };
let toastTimer = 0;
let resultTimer = 0;
let voiceAuditionRun = 0;
let voiceStudioReturnToPicker = false;
let lastHomeTouchRole = '';
let lastHomeTouchAt = 0;

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('Missing app root');

function temperament(item: Difficulty, compact = false): string {
  return `<div class="temperament${compact ? ' compact' : ''}" aria-label="${item.styleLabel}：攻勢 ${item.attack}，守勢 ${item.defense}">
    <div class="temperament-row attack"><span>攻勢</span><i><b style="width:${item.attack}%"></b></i></div>
    <div class="temperament-row defense"><span>守勢</span><i><b style="width:${item.defense}%"></b></i></div>
    <strong class="style-type">${item.styleLabel}</strong>
  </div>`;
}

const strengthRows = DIFFICULTIES.map((item) => `
  <article class="strength-row" style="--accent:${item.accent}">
    <img src="${item.image}" alt="${item.name}角色肖像" width="88" height="88">
    <div><div class="strength-heading"><strong>${item.name}</strong><span>${item.rank}</span></div><p>${item.strengthLabel}</p>${temperament(item)}<small>${item.playStyle}</small></div>
  </article>`).join('');

const voiceStudioRows = DIFFICULTIES.map((item) => `
  <article class="voice-character" style="--accent:${item.accent}">
    <header><img src="${item.image}" alt="${item.name}角色肖像" width="72" height="72"><div><small>${item.rank}</small><h3>${item.name}</h3><p>${item.styleLabel}・${item.title}</p></div></header>
    <div class="voice-lines">${VOICE_CUES.map((cue) => `<div class="voice-line" data-voice-row="${item.id}-${cue}"><span>${VOICE_CUE_LABEL[cue]}</span><p>${VOICE_LINES[item.id][cue]}</p><button class="voice-play-btn" type="button" data-voice-character="${item.id}" data-voice-cue="${cue}" aria-label="播放${item.name}${VOICE_CUE_LABEL[cue]}配音"><i aria-hidden="true">▶</i><b>播放</b></button></div>`).join('')}</div>
  </article>`).join('');

const nativeVoicePreviews = DIFFICULTIES.map((item) => `
  <article class="native-preview-card" data-native-row="${item.id}" style="--accent:${item.accent}">
    <img src="${item.image}" alt="${item.name}角色肖像" width="58" height="58">
    <div><strong>${item.name}</strong><small>${VOICE_CAST[item.id].label}・seed ${VOICE_CAST[item.id].seed}</small><p>${VOICE_LINES[item.id].intro}</p></div>
    <button type="button" data-native-preview="${item.id}" aria-label="播放${item.name}本機 VoiceDesign 聲線試音"><i aria-hidden="true">▶</i><b>角色聲線試音</b></button>
  </article>`).join('');

const coverCharacters = DIFFICULTIES.map((item, index) => `
  <button type="button" class="cover-role cover-role-${index + 1}" data-home-role="${item.id}" style="--accent:${item.accent}" aria-label="${item.name}；單擊選擇並試聽，雙擊或按 Enter 進入對局" aria-pressed="false">
    <span class="cover-role-frame"><img src="${item.cardImage}" alt="${item.name}完整角色卡，${item.styleLabel}，${item.rank}" width="600" height="1080"></span>
  </button>`).join('');

app.innerHTML = `
  <section id="homeScreen" class="home-screen" aria-labelledby="homeTitle">
    <div class="home-board-lines" aria-hidden="true"></div>
    <header class="home-masthead"><span class="home-seal">五</span><p>RAPFI GOMOKU<br><b>FANTASYMAYA</b></p><span class="home-edition">15 × 15<br>FIVE GUARDIANS</span></header>
    <div class="home-stage">
      <div class="home-copy">
        <p class="home-kicker">THE FIVE STONES OF CLOUD GATE</p>
        <h1 id="homeTitle"><span>五石</span><span>雲關</span></h1>
        <p class="home-subtitle">十五路棋盤，五位守局人。<br>穿越雲關，落下決定勝負的第五顆棋子。</p>
        <div id="homeSelection" class="home-selection" aria-live="polite">
          <div class="home-selection-heading"><span><small id="homeSelectionRank"></small><strong id="homeSelectionName"></strong></span><b id="homeSelectionType"></b></div>
          <div class="home-selection-bars"><span>攻勢<i><b id="homeAttackBar"></b></i></span><span>守勢<i><b id="homeDefenseBar"></b></i></span></div>
          <p id="homeSelectionLine">點選右側角色，試聽登場語音。</p>
        </div>
        <div class="home-secondary-actions">
          <button id="homeRulesBtn" type="button">規則與棋力</button>
          <button id="homeTrainingBtn" type="button">棋形訓練</button>
          <button id="homeVoiceStudioBtn" type="button">角色配音</button>
        </div>
      </div>
      <div id="coverCast" class="cover-cast" aria-label="選擇五位雲關守局人">${coverCharacters}<p class="home-entry-hint"><span>單擊・選角試音</span><strong>雙擊角色・直接入局</strong><small>觸控連點兩下｜鍵盤 Enter</small></p></div>
    </div>
    <footer class="home-footer"><span>開源 Rapfi AI・自由／標準／連珠規則</span><span>棋局完全在瀏覽器內運作</span></footer>
  </section>

  <div class="app-shell">
    <header class="topbar">
      <button id="homeBtn" class="brand home-brand" type="button" aria-label="返回主畫面"><span class="brand-mark">五</span><span><strong>五石雲關</strong><small>FIVE STONES OF CLOUD GATE</small></span></button>
      <div class="top-actions">
        <button id="musicBtn" class="icon-btn" type="button" aria-label="切換背景音樂"><span aria-hidden="true">♫</span><b>音樂</b></button>
        <button id="soundBtn" class="icon-btn" type="button" aria-label="切換遊戲音效"><span aria-hidden="true">◉</span><b>音效</b></button>
        <button id="voiceBtn" class="icon-btn" type="button" aria-label="切換角色配音"><span aria-hidden="true">♬</span><b>配音</b></button>
        <button id="fullscreenBtn" class="icon-btn" type="button" aria-label="切換瀏覽器全螢幕"><span aria-hidden="true">⛶</span><b>全螢幕</b></button>
        <div id="engineBadge" class="engine-badge loading" role="status"><span class="engine-dot"></span><span id="engineText">正在載入 Rapfi</span></div>
      </div>
    </header>
    <main class="game-layout">
      <aside class="glass-card side-panel" aria-label="選擇守局人與規則">
        <div class="section-title"><h2>目前對手</h2><button id="expandPickerBtn" class="text-btn" type="button">更換角色</button></div>
        <div class="fullscreen-match-dock" aria-live="polite">
          <div class="fullscreen-rival"><img id="fullscreenPortrait" src="${difficulty.image}" alt="" width="52" height="52"><div class="fullscreen-rival-copy"><small id="fullscreenOpponentRank">${difficulty.rank}・${difficulty.styleLabel}</small><b id="fullscreenOpponentName">${difficulty.name}</b><span id="fullscreenMeta"></span><strong id="fullscreenTurnText"></strong></div></div>
          <div class="fullscreen-role-stats"><div id="fullscreenTemperament">${temperament(difficulty, true)}</div><small id="fullscreenStrength">${difficulty.strengthLabel}</small></div>
          <div id="fullscreenStyleInsight" class="fullscreen-style-insight"></div>
        </div>
        <p class="ability-key"><i class="attack-dot"></i>攻勢 <i class="defense-dot"></i>守勢</p>
        <div id="opponentList" class="opponent-list current-only"></div>
        <div id="opponentDetail" class="opponent-detail"></div>
        <div id="profileSummary" class="profile-summary"></div>
        <div class="settings">
          <div class="field"><label for="ruleSelect">對局規則</label><select id="ruleSelect"><option value="freestyle">自由・五子以上</option><option value="standard">標準・剛好五子</option><option value="renju">連珠・黑棋禁手</option></select></div>
          <div class="field"><label for="sideSelect">你的棋色</label><select id="sideSelect"><option value="1">執黑・先行</option><option value="2">執白・後行</option></select></div>
        </div>
      </aside>
      <section class="board-column" aria-label="五子棋對局">
        <div class="glass-card match-bar"><div class="rival-mini"><img id="matchPortrait" src="${difficulty.image}" alt="" width="44" height="44"><div class="turn-copy"><small id="matchMeta">自由規則・第 1 手</small><strong id="turnText">黑方先行，請落子</strong></div></div><div class="stone-key"><span><i class="mini-stone black"></i>黑</span><span><i class="mini-stone white"></i>白</span></div></div>
        <div id="styleInsight" class="style-insight" aria-live="polite"></div>
        <div class="board-frame">
          <div class="board-surface" id="boardSurface">
            <span class="star" style="left:23.333%;top:23.333%"></span><span class="star" style="left:76.667%;top:23.333%"></span><span class="star" style="left:50%;top:50%"></span><span class="star" style="left:23.333%;top:76.667%"></span><span class="star" style="left:76.667%;top:76.667%"></span>
            <div id="boardGrid" class="board-grid" role="grid" aria-label="15 路五子棋盤"></div>
          </div>
        </div>
        <div id="replayPanel" class="replay-panel" aria-live="polite"><button id="replayPrevBtn" type="button">← 上一手</button><strong id="replayCounter">0 / 0</strong><button id="replayNextBtn" type="button">下一手 →</button><button id="replayExitBtn" type="button">離開回放</button></div>
        <div class="controls"><button id="newGameBtn" class="control-btn primary">重新開局</button><button id="undoBtn" class="control-btn">悔一回合</button><button id="swapBtn" class="control-btn">交換棋色</button><button id="returnHomeBtn" class="control-btn">返回主畫面</button></div>
      </section>
      <aside class="glass-card side-panel record-panel" aria-label="對局紀錄">
        <div><div class="section-title"><h2>行棋紀錄</h2><span>GAME RECORD</span></div><div class="stats-grid"><div class="stat"><strong id="moveCount">0</strong><small>手數</small></div><div class="stat"><strong id="humanCount">0</strong><small>你的棋</small></div><div class="stat"><strong id="aiCount">0</strong><small>守局人</small></div></div></div>
        <ol id="history" class="history"><li class="empty">棋盤尚未落子</li></ol>
        <blockquote id="quote" class="quote">「棋盤不催你。先看見對手下一手，再決定自己的這一手。」</blockquote>
        <button id="rulesLinkBtn" class="license-link button-link" type="button">規則與棋力說明</button>
        <button id="trainingLinkBtn" class="license-link button-link" type="button">棋形訓練與每日挑戰</button>
        <button id="voiceStudioLinkBtn" class="license-link button-link" type="button">五位角色・20 段配音試聽</button>
        <a class="license-link" href="/licenses/NOTICE.md" target="_blank" rel="noopener">Rapfi・GPL v3・開源授權說明 ↗</a>
      </aside>
    </main>
    <footer class="footer">五石雲關・Five Stones of Cloud Gate v5.7｜Rapfi 在瀏覽器內運作，角色配音由本機 Qwen VoiceDesign 產生</footer>
    <div id="toast" class="toast" role="status"></div>
  </div>

  <section id="characterPicker" class="modal-screen character-picker" role="dialog" aria-modal="true" aria-labelledby="pickerTitle" aria-hidden="true">
    <div class="modal-backdrop"></div>
    <div class="modal-content picker-content">
      <header class="modal-header"><div><p class="eyebrow">CHOOSE YOUR RIVAL</p><h2 id="pickerTitle">選擇守局人</h2><p>同一套 Rapfi 引擎，搭配不同棋力與候選著偏好，形成五種實際棋風。</p></div><button id="closePickerBtn" class="close-btn" type="button" aria-label="關閉角色選擇">×</button></header>
      <div id="pickerGrid" class="picker-grid"></div>
      <div class="picker-footer"><button id="pickerVoiceStudioBtn" type="button">先試聽五位角色配音</button><p class="picker-note">攻勢／守勢表示候選著的偏好，不是勝率；立即取勝與擋殺仍優先。Rapfi 百分比不等同公開平台 Elo。</p></div>
    </div>
  </section>

  <section id="rulesModal" class="modal-screen rules-modal" role="dialog" aria-modal="true" aria-labelledby="rulesTitle" aria-hidden="true">
    <div class="modal-backdrop"></div>
    <div class="modal-content rules-content">
      <header class="modal-header"><div><p class="eyebrow">RULES & STRENGTH</p><h2 id="rulesTitle">規則與角色棋力</h2><p>先理解勝負條件，再挑選適合練習目標的守局人。</p></div><button id="closeRulesBtn" class="close-btn" type="button" aria-label="關閉規則說明">×</button></header>
      <div class="rules-layout">
        <section class="rule-copy"><h3>基本下法</h3><ol><li>棋盤為 15 × 15 路，黑棋先行，雙方輪流在交叉點落一子。</li><li>先在橫、直或斜線連成五子的一方獲勝；不同模式對長連與黑棋禁手有不同判定。</li><li>棋子落下後不能移動。悔棋會退回上一個完整回合。</li></ol><div class="rule-cards"><article><b>自由規則</b><p>黑白雙方連成五子或更多都算勝利，最適合初學。</p></article><article><b>標準規則</b><p>必須剛好五子；六子以上的長連不算勝利。</p></article><article><b>連珠規則</b><p>黑棋有三三、四四與長連禁手；白棋沒有禁手，五子以上可勝。</p></article></div><div class="renju-note"><strong>禁手提示</strong><p>連珠模式輪到黑棋時，Rapfi 會在棋盤上以「×」標出不可落子的點。</p></div></section>
        <section class="strength-copy"><h3>五位守局人的棋力與棋風</h3><div class="strength-list">${strengthRows}</div><p class="elo-note">這裡不標示虛構 Elo。棋力由 Rapfi 的 <code>INFO STRENGTH</code> 與每手時間控制；棋風則從 Rapfi 前十個候選著中，在不漏掉立即勝負的前提下，依攻勢／守勢與變奏偏好選擇。</p></section>
      </div>
    </div>
  </section>`;

app.insertAdjacentHTML('beforeend', `
  <section id="voiceStudioModal" class="modal-screen voice-studio-modal" role="dialog" aria-modal="true" aria-labelledby="voiceStudioTitle" aria-hidden="true">
    <div class="modal-backdrop"></div>
    <div class="modal-content voice-studio-content">
      <header class="modal-header"><div><p class="eyebrow">VOICE AUDITION</p><h2 id="voiceStudioTitle">配音試聽室</h2><p>五位守局人，每位四段台詞。可以逐句播放，也可以一次聽完 20 段。</p></div><button id="closeVoiceStudioBtn" class="close-btn" type="button" aria-label="關閉配音試聽室">×</button></header>
      <div class="voice-studio-toolbar"><button id="playAllVoicesBtn" class="control-btn" type="button">▶ 播放新版 20 段</button><button id="stopVoicesBtn" class="control-btn" type="button">■ 停止播放</button><p id="voiceStudioStatus" role="status" aria-live="polite">目前遊戲已全面使用本機 Qwen VoiceDesign 角色配音。</p></div>
      <section class="native-preview-section" aria-labelledby="nativePreviewTitle"><div class="native-preview-heading"><div><p class="eyebrow">LOCAL QWEN VOICE DESIGN</p><h3 id="nativePreviewTitle">五位角色專屬聲線</h3><strong class="voice-version-badge">${VOICE_CAST_VERSION}</strong></div><p>聲音以本機 Qwen3-TTS 1.7B VoiceDesign 依角色設定產生，不使用真人錄音或聲音複製；遊戲內播放的就是下方聲線。</p></div><div class="native-preview-grid">${nativeVoicePreviews}</div></section>
      <h3 class="voice-list-title">遊戲目前使用・20 段完整台詞</h3>
      <div class="voice-character-list">${voiceStudioRows}</div>
    </div>
  </section>

  <section id="resultDialog" class="modal-screen result-dialog" role="dialog" aria-modal="true" aria-labelledby="resultTitle" aria-hidden="true">
    <div class="modal-backdrop"></div>
    <div class="result-card" style="--accent:${difficulty.accent}">
      <img id="resultPortrait" src="${difficulty.image}" alt="${difficulty.name}角色肖像" width="128" height="128">
      <p id="resultEyebrow" class="eyebrow">MATCH COMPLETE</p>
      <h2 id="resultTitle">本局終了</h2>
      <p id="resultCopy"></p>
      <div class="result-actions">
        <button id="resultRematchBtn" class="control-btn primary" type="button">再戰一局</button>
        <button id="resultUndoBtn" class="control-btn" type="button">悔回勝負手</button>
        <button id="resultCharacterBtn" class="control-btn" type="button">更換角色</button>
        <button id="resultBoardBtn" class="control-btn" type="button">棋局回放</button>
      </div>
    </div>
  </section>

  <section id="trainingModal" class="modal-screen training-modal" role="dialog" aria-modal="true" aria-labelledby="trainingTitle" aria-hidden="true">
    <div class="modal-backdrop"></div>
    <div class="modal-content training-content">
      <header class="modal-header"><div><p class="eyebrow">PATTERN TRAINING</p><h2 id="trainingTitle">棋形訓練場</h2><p>先看見威脅，再決定落子。每日一題會依台灣本地日期更新。</p></div><button id="closeTrainingBtn" class="close-btn" type="button" aria-label="關閉棋形訓練">×</button></header>
      <div class="training-layout"><aside><div id="trainingProgress" class="training-progress"></div><div id="trainingList" class="training-list"></div></aside><section class="training-stage"><div id="trainingHeading" class="training-heading"></div><div id="trainingBoard" class="training-board" role="grid" aria-label="棋形訓練棋盤"></div><p id="trainingFeedback" class="training-feedback" aria-live="polite"></p></section></div>
    </div>
  </section>`);

function query<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing ${selector}`);
  return element;
}

const boardGrid = query<HTMLDivElement>('#boardGrid');
const engineBadge = query<HTMLDivElement>('#engineBadge');
const engineText = query<HTMLSpanElement>('#engineText');
const turnText = query<HTMLElement>('#turnText');
const matchMeta = query<HTMLElement>('#matchMeta');
const history = query<HTMLOListElement>('#history');
const quote = query<HTMLElement>('#quote');
const newGameBtn = query<HTMLButtonElement>('#newGameBtn');
const undoBtn = query<HTMLButtonElement>('#undoBtn');
const swapBtn = query<HTMLButtonElement>('#swapBtn');
const ruleSelect = query<HTMLSelectElement>('#ruleSelect');
const sideSelect = query<HTMLSelectElement>('#sideSelect');
const characterPicker = query<HTMLElement>('#characterPicker');
const rulesModal = query<HTMLElement>('#rulesModal');
const voiceStudioModal = query<HTMLElement>('#voiceStudioModal');
const resultDialog = query<HTMLElement>('#resultDialog');
const trainingModal = query<HTMLElement>('#trainingModal');
const audio = new AudioDirector();

const engine = new RapfiClient();
engine.onUpdate((update) => { engineState = update; renderEngine(); });

function renderEngine(): void {
  engineBadge.className = `engine-badge ${engineState.status}`;
  engineText.textContent = engineState.message;
  if (typeof engineState.progress === 'number') engineText.textContent += ` ${Math.round(engineState.progress * 100)}%`;
}

function renderAudioControls(): void {
  const entries: Array<[string, 'music' | 'sound' | 'voice']> = [['#musicBtn', 'music'], ['#soundBtn', 'sound'], ['#voiceBtn', 'voice']];
  entries.forEach(([selector, key]) => {
    const button = query<HTMLButtonElement>(selector);
    button.setAttribute('aria-pressed', String(audio.prefs[key]));
    button.classList.toggle('is-off', !audio.prefs[key]);
  });
  const fullscreen = Boolean(document.fullscreenElement);
  query<HTMLButtonElement>('#fullscreenBtn').setAttribute('aria-pressed', String(fullscreen));
  query<HTMLButtonElement>('#fullscreenBtn').classList.toggle('is-active', fullscreen);
}

function renderHomeSelection(): void {
  const cast = query<HTMLElement>('#coverCast');
  cast.classList.add('has-selection');
  document.querySelectorAll<HTMLButtonElement>('[data-home-role]').forEach((role) => {
    const selected = role.dataset.homeRole === difficulty.id;
    role.classList.toggle('is-selected', selected);
    role.setAttribute('aria-pressed', String(selected));
  });
  const panel = query<HTMLElement>('#homeSelection');
  panel.style.setProperty('--accent', difficulty.accent);
  query<HTMLElement>('#homeSelectionRank').textContent = difficulty.rank;
  query<HTMLElement>('#homeSelectionName').textContent = difficulty.name;
  query<HTMLElement>('#homeSelectionType').textContent = difficulty.styleLabel;
  query<HTMLElement>('#homeAttackBar').style.width = `${difficulty.attack}%`;
  query<HTMLElement>('#homeDefenseBar').style.width = `${difficulty.defense}%`;
  query<HTMLElement>('#homeSelectionLine').textContent = `${difficulty.title}・${difficulty.playStyle}`;
}

function renderOpponents(): void {
  query<HTMLDivElement>('#opponentList').innerHTML = `
    <article class="opponent-card current-opponent" style="--accent:${difficulty.accent}">
      <img class="opponent-avatar" src="${difficulty.image}" alt="${difficulty.name}角色肖像" width="48" height="48"><span class="opponent-copy"><strong>${difficulty.name}</strong><small>${difficulty.rank}・${difficulty.styleLabel}</small>${temperament(difficulty, true)}</span>
    </article>`;
  query<HTMLDivElement>('#opponentDetail').innerHTML = `<div class="detail-heading"><b>${difficulty.title}</b><span>${difficulty.strengthLabel}</span></div>${temperament(difficulty, true)}<p>${difficulty.description}</p><small>${difficulty.playStyle}</small>`;
  query<HTMLImageElement>('#matchPortrait').src = difficulty.image;
  query<HTMLImageElement>('#matchPortrait').alt = `${difficulty.name}角色肖像`;
  query<HTMLImageElement>('#fullscreenPortrait').src = difficulty.image;
  query<HTMLImageElement>('#fullscreenPortrait').alt = `${difficulty.name}角色肖像`;
  query<HTMLElement>('#fullscreenOpponentRank').textContent = `${difficulty.rank}・${difficulty.styleLabel}`;
  query<HTMLElement>('#fullscreenOpponentName').textContent = difficulty.name;
  query<HTMLElement>('#fullscreenTemperament').innerHTML = temperament(difficulty, true);
  query<HTMLElement>('#fullscreenStrength').textContent = difficulty.strengthLabel;
}

function renderPicker(): void {
  query<HTMLDivElement>('#pickerGrid').innerHTML = DIFFICULTIES.map((item) => `
    <button class="picker-card${item.id === difficulty.id ? ' selected' : ''}" data-pick="${item.id}" style="--accent:${item.accent}" aria-pressed="${item.id === difficulty.id}">
      <img src="${item.image}" alt="${item.name}角色肖像" width="720" height="720">
      <span class="picker-gradient"></span><span class="picker-copy"><small>${item.rank}</small><strong>${item.name}</strong><em>${item.english}</em><span>${item.title}</span><span class="picker-strength">${item.strengthLabel}</span>${temperament(item)}<span class="picker-style">${item.playStyle}</span><b>${item.id === difficulty.id ? '目前對手' : '選擇迎戰'}</b></span>
    </button>`).join('');
}

function visibleMoves(): Move[] {
  return replayMode ? replayMoves.slice(0, replayIndex) : moves;
}

function renderBoard(): void {
  const shownMoves = visibleMoves();
  const board = boardFromMoves(shownMoves);
  const last = shownMoves.at(-1);
  if (boardGrid.children.length !== BOARD_SIZE * BOARD_SIZE) {
    boardGrid.replaceChildren();
    const cells = document.createDocumentFragment();
    for (let y = 0; y < BOARD_SIZE; y += 1) {
      for (let x = 0; x < BOARD_SIZE; x += 1) {
        const cell = document.createElement('button');
        cell.className = 'intersection';
        cell.dataset.x = String(x);
        cell.dataset.y = String(y);
        cell.setAttribute('role', 'gridcell');
        cells.append(cell);
      }
    }
    boardGrid.append(cells);
  }
  for (let y = 0; y < BOARD_SIZE; y += 1) {
    for (let x = 0; x < BOARD_SIZE; x += 1) {
      const cell = boardGrid.children[y * BOARD_SIZE + x] as HTMLButtonElement;
      const stone = board[y][x];
      cell.className = `intersection${forbidden.has(`${x},${y}`) ? ' forbidden' : ''}`;
      cell.setAttribute('aria-label', stone ? `${notation({ x, y, side: stone })}，${stone === 1 ? '黑子' : '白子'}` : `${String.fromCharCode(65 + x)}${BOARD_SIZE - y}，空位`);
      cell.disabled = Boolean(replayMode || stone || thinking || gameOver || forbiddenUnavailable || nextSide(moves) !== humanSide || forbidden.has(`${x},${y}`));
      cell.replaceChildren();
      if (stone) {
        const piece = document.createElement('span');
        piece.className = `stone ${stone === 1 ? 'black' : 'white'}${last?.x === x && last?.y === y ? ' last' : ''}${replayIndex === replayMoves.length && winning.has(`${x},${y}`) ? ' win' : ''}`;
        cell.append(piece);
      }
    }
  }
}

function renderInsight(): void {
  const element = query<HTMLDivElement>('#styleInsight');
  const fullscreenElement = query<HTMLDivElement>('#fullscreenStyleInsight');
  element.classList.toggle('visible', Boolean(moveInsight));
  fullscreenElement.classList.toggle('visible', Boolean(moveInsight));
  const content = moveInsight ? `<strong>${moveInsight.label}</strong><span>${moveInsight.message}</span>` : '';
  element.innerHTML = content;
  fullscreenElement.innerHTML = content;
}

function syncFullscreenStatus(): void {
  query<HTMLElement>('#fullscreenMeta').textContent = matchMeta.textContent?.replace(`${difficulty.name}・`, '') ?? '';
  query<HTMLElement>('#fullscreenTurnText').textContent = turnText.textContent;
}

function renderStatus(): void {
  if (replayMode) {
    matchMeta.textContent = `${difficulty.name}・棋局回放`;
    turnText.textContent = replayIndex ? `第 ${replayIndex} 手・${notation(replayMoves[replayIndex - 1])}` : '開局前・尚未落子';
    syncFullscreenStatus();
    return;
  }
  const side = nextSide(moves);
  matchMeta.textContent = `${difficulty.name}・${RULE_LABEL[rule]}・第 ${moves.length + 1} 手`;
  if (gameOver) { syncFullscreenStatus(); return; }
  if (thinking) turnText.textContent = `${difficulty.name}正在推演棋路…`;
  else if (side === humanSide) turnText.textContent = `輪到你・${humanSide === 1 ? '黑方' : '白方'}落子`;
  else turnText.textContent = `等待${difficulty.name}落子`;
  syncFullscreenStatus();
}

function renderHistory(): void {
  const shownMoves = visibleMoves();
  query<HTMLElement>('#moveCount').textContent = String(shownMoves.length);
  query<HTMLElement>('#humanCount').textContent = String(shownMoves.filter((move) => move.side === humanSide).length);
  query<HTMLElement>('#aiCount').textContent = String(shownMoves.filter((move) => move.side !== humanSide).length);
  history.innerHTML = shownMoves.length ? shownMoves.map((move, index) => `<li><span class="move-no">${index + 1}</span><i class="mini-stone ${move.side === 1 ? 'black' : 'white'}"></i><span>${notation(move)}</span></li>`).join('') : '<li class="empty">棋盤尚未落子</li>';
  history.scrollTop = history.scrollHeight;
}

function renderProfile(): void {
  const record = roleRecord(profile, difficulty.id);
  const progress = mastery(profile, difficulty);
  query<HTMLDivElement>('#profileSummary').innerHTML = `<div><span>角色戰績</span><strong>${record.wins} 勝・${record.losses} 負・${record.draws} 和</strong></div><div class="mastery-heading"><span>${progress.label}</span><b>${progress.points}%</b></div><i class="mastery-meter"><b style="width:${progress.points}%"></b></i>`;
}

function renderReplay(): void {
  const panel = query<HTMLDivElement>('#replayPanel');
  panel.classList.toggle('visible', replayMode);
  query<HTMLElement>('#replayCounter').textContent = `${replayIndex} / ${replayMoves.length}`;
  query<HTMLButtonElement>('#replayPrevBtn').disabled = replayIndex <= 0;
  query<HTMLButtonElement>('#replayNextBtn').disabled = replayIndex >= replayMoves.length;
}

function renderTraining(): void {
  const today = localDateKey();
  const daily = dailyPuzzle();
  const solved = new Set(profile.solvedPuzzles);
  query<HTMLDivElement>('#trainingProgress').innerHTML = `<span>修習進度</span><strong>${solved.size} / ${TRAINING_PUZZLES.length} 式</strong><small>${profile.dailyWins.includes(today) ? '今日挑戰已完成' : '今日挑戰尚未完成'}</small>`;
  query<HTMLDivElement>('#trainingList').innerHTML = TRAINING_PUZZLES.map((puzzle) => `<button type="button" data-training-id="${puzzle.id}" class="training-item${puzzle.id === selectedPuzzle.id ? ' selected' : ''}"><span>${puzzle.id === daily.id ? '今日・' : ''}${puzzle.rank}</span><strong>${puzzle.title}</strong><small>${puzzle.concept}${solved.has(puzzle.id) ? '・已解' : ''}</small></button>`).join('');
  query<HTMLDivElement>('#trainingHeading').innerHTML = `<div><p class="eyebrow">${selectedPuzzle.rank}・${selectedPuzzle.concept}</p><h3>${selectedPuzzle.title}</h3><p>${selectedPuzzle.description}</p></div><button id="trainingHintBtn" type="button">提示</button>`;
  const board = boardFromMoves(selectedPuzzle.setup);
  query<HTMLDivElement>('#trainingBoard').innerHTML = board.flatMap((row, y) => row.map((stone, x) => `<button type="button" class="training-intersection${trainingSolved && isPuzzleSolution(selectedPuzzle, x, y) ? ' solution' : ''}" data-training-x="${x}" data-training-y="${y}" aria-label="${String.fromCharCode(65 + x)}${BOARD_SIZE - y}${stone ? `，${stone === 1 ? '黑子' : '白子'}` : '，空位'}" ${stone || trainingSolved ? 'disabled' : ''}>${stone ? `<i class="stone ${stone === 1 ? 'black' : 'white'}"></i>` : ''}</button>`)).join('');
  const feedback = query<HTMLElement>('#trainingFeedback');
  feedback.textContent = trainingMessage || '你執黑。請在棋盤上選擇關鍵落點。';
  feedback.classList.toggle('success', trainingSolved);
}

function render(): void {
  renderHomeSelection(); renderOpponents(); renderPicker(); renderBoard(); renderStatus(); renderHistory(); renderInsight(); renderProfile(); renderReplay(); renderTraining(); renderEngine(); renderAudioControls();
  undoBtn.disabled = replayMode || thinking || moves.length === 0; newGameBtn.disabled = thinking; swapBtn.disabled = replayMode || thinking || moves.length > 0;
  ruleSelect.disabled = thinking || moves.length > 0; sideSelect.disabled = thinking || moves.length > 0;
}

function toast(message: string): void {
  const element = query<HTMLDivElement>('#toast'); element.textContent = message; element.classList.add('visible');
  window.clearTimeout(toastTimer); toastTimer = window.setTimeout(() => element.classList.remove('visible'), 2600);
}

function saveGame(): void {
  const saved: SavedGame = { version: 1, moves, rule, humanSide, difficultyId: difficulty.id };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
}

function loadGame(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY); if (!raw) return;
    const saved = JSON.parse(raw) as SavedGame; if (saved.version !== 1 || !Array.isArray(saved.moves)) return;
    moves = saved.moves.filter((move) => Number.isInteger(move.x) && Number.isInteger(move.y) && (move.side === 1 || move.side === 2));
    rule = saved.rule in RULE_LABEL ? saved.rule : 'freestyle'; humanSide = saved.humanSide === 2 ? 2 : 1;
    difficulty = difficultyById(saved.difficultyId) ?? DIFFICULTIES[1]; ruleSelect.value = rule; sideSelect.value = String(humanSide);
  } catch { localStorage.removeItem(STORAGE_KEY); }
}

function scheduleResult(outcome: MatchOutcome): void {
  window.clearTimeout(resultTimer);
  resultTimer = window.setTimeout(() => {
    profileBeforeResult = structuredClone(profile);
    profile = recordMatch(profile, difficulty.id, outcome);
    saveProfile(profile);
    renderProfile();
    const title = query<HTMLElement>('#resultTitle');
    const copy = query<HTMLElement>('#resultCopy');
    const eyebrow = query<HTMLElement>('#resultEyebrow');
    const card = query<HTMLElement>('.result-card');
    const portrait = query<HTMLImageElement>('#resultPortrait');
    card.style.setProperty('--accent', difficulty.accent);
    portrait.src = difficulty.image;
    portrait.alt = `${difficulty.name}角色肖像`;
    eyebrow.textContent = outcome === 'win' ? 'VICTORY' : outcome === 'lose' ? 'DEFEAT' : 'DRAW';
    title.textContent = outcome === 'win' ? '雲關為你而開' : outcome === 'lose' ? `${difficulty.name}守住此局` : '本局和棋';
    copy.textContent = outcome === 'win'
      ? `你在第 ${moves.length} 手完成五連。要趁手感再戰，還是回看最後的勝負手？`
      : outcome === 'lose'
        ? `勝負往往早在最後一手以前形成。可以悔回關鍵回合，再試一次。`
        : '棋盤已滿。換一種棋風或棋色，再開新局。';
    setModal(resultDialog, true);
    query<HTMLButtonElement>('#resultRematchBtn').focus();
  }, 760);
}

function finishIfNeeded(move: Move): boolean {
  const line = winningLine(boardFromMoves(moves), move, rule);
  if (line) {
    gameOver = true; winning = new Set(line.map(([x, y]) => `${x},${y}`));
    replayMoves = [...moves]; replayIndex = replayMoves.length;
    const humanWon = move.side === humanSide;
    turnText.textContent = humanWon ? '你已連成五子，雲關為你而開' : `${difficulty.name}守住了這一局`;
    quote.textContent = humanWon ? '「真正的好棋，不只是看見五子，而是讓五子成為唯一的答案。」' : '「輸掉的那一手，通常比棋盤上看到的更早。回看棋路，再走一次。」';
    toast(humanWon ? '勝局・你已連成五子' : '本局由守局人取勝'); audio.playOutcome(humanWon);
    window.setTimeout(() => audio.playVoice(difficulty.id, humanWon ? 'win' : 'lose'), 480); scheduleResult(humanWon ? 'win' : 'lose'); saveGame(); return true;
  }
  if (isBoardFull(moves)) { gameOver = true; replayMoves = [...moves]; replayIndex = replayMoves.length; turnText.textContent = '棋盤已滿，本局和棋'; toast('和局・棋盤已滿'); scheduleResult('draw'); return true; }
  return false;
}

async function refreshForbidden(): Promise<void> {
  forbiddenUnavailable = false;
  if (rule !== 'renju' || nextSide(moves) !== 1) { forbidden = new Set(); return; }
  try { forbidden = await engine.forbiddenMoves(moves); }
  catch {
    forbidden = new Set(); forbiddenUnavailable = true;
    toast('禁手判定暫時無法完成，已停止落子；請重新開局後再試');
  }
}

function fallbackMove(): [number, number] {
  const board = boardFromMoves(moves); const center = 7; const options: Array<[number, number]> = [];
  for (let radius = 0; radius < BOARD_SIZE; radius += 1) {
    for (let y = 0; y < BOARD_SIZE; y += 1) for (let x = 0; x < BOARD_SIZE; x += 1) if (!board[y][x] && Math.max(Math.abs(x - center), Math.abs(y - center)) === radius) options.push([x, y]);
    if (options.length) break;
  }
  return options[Math.floor(Math.random() * options.length)] ?? [center, center];
}

async function aiTurn(): Promise<void> {
  if (replayMode || gameOver || nextSide(moves) === humanSide) return;
  thinking = true; render(); let position: [number, number];
  try { position = await engine.think(moves, rule, difficulty); }
  catch { position = fallbackMove(); toast('Rapfi 暫時無法使用，這手改由離線備援落子'); }
  moveInsight = explainStyledMove(moves, rule, difficulty, position);
  const side = nextSide(moves); moves = [...moves, { x: position[0], y: position[1], side }]; audio.playStone(side); thinking = false;
  const ended = finishIfNeeded(moves.at(-1)!); if (!ended) await refreshForbidden(); saveGame(); render();
}

async function humanMove(x: number, y: number): Promise<void> {
  if (replayMode || thinking || gameOver || nextSide(moves) !== humanSide) return;
  const board = boardFromMoves(moves);
  if (board[y][x] || forbidden.has(`${x},${y}`)) { if (forbidden.has(`${x},${y}`)) toast('此點是黑棋禁手'); return; }
  const move: Move = { x, y, side: humanSide }; moves = [...moves, move]; forbidden = new Set(); moveInsight = null; audio.playStone(humanSide); saveGame(); render();
  if (!finishIfNeeded(move)) await aiTurn(); render();
}

function newGame(message = '新局已開始'): void {
  window.clearTimeout(resultTimer); setModal(resultDialog, false);
  moves = []; forbidden = new Set(); forbiddenUnavailable = false; winning = new Set(); gameOver = false; moveInsight = null;
  replayMode = false; replayMoves = []; replayIndex = 0; profileBeforeResult = null;
  quote.textContent = '「棋盤不催你。先看見對手下一手，再決定自己的這一手。」'; saveGame(); render(); toast(message);
  if (humanSide === 2) void aiTurn();
}

function setModal(element: HTMLElement, open: boolean): void {
  element.classList.toggle('is-open', open); element.setAttribute('aria-hidden', String(!open));
  document.body.classList.toggle('modal-open', characterPicker.classList.contains('is-open') || rulesModal.classList.contains('is-open') || voiceStudioModal.classList.contains('is-open') || resultDialog.classList.contains('is-open') || trainingModal.classList.contains('is-open'));
}

function enterMatch(): void {
  document.body.classList.remove('home-active');
  document.body.classList.add('match-active');
  window.requestAnimationFrame(() => query<HTMLElement>('.game-layout').scrollIntoView({ block: 'start' }));
}

function goHome(): void {
  window.clearTimeout(resultTimer);
  audio.stopVoice();
  setModal(characterPicker, false); setModal(rulesModal, false); setModal(voiceStudioModal, false); setModal(resultDialog, false); setModal(trainingModal, false);
  document.body.classList.remove('match-active');
  document.body.classList.add('home-active');
  query<HTMLElement>('#homeScreen').scrollTo({ top: 0, left: 0, behavior: 'auto' });
  window.scrollTo(0, 0);
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  window.requestAnimationFrame(() => {
    query<HTMLElement>('#homeScreen').scrollTo({ top: 0, left: 0, behavior: 'auto' });
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  });
}

function chooseCharacter(id: string, close = false): void {
  const selected = difficultyById(id); if (!selected || thinking) return;
  difficulty = selected; newGame(`已選擇${difficulty.name}`); audio.playVoice(difficulty.id, 'intro'); if (close) { setModal(characterPicker, false); enterMatch(); }
}

function selectHomeCharacter(id: string): void {
  const selected = difficultyById(id); if (!selected || thinking) return;
  difficulty = selected;
  renderHomeSelection(); renderOpponents(); renderPicker(); renderProfile(); renderStatus();
  audio.enableVoice(); renderAudioControls(); audio.playUi();
  void audio.playVoice(difficulty.id, 'intro');
}

function startHomeMatch(id = difficulty.id): void {
  if (id !== difficulty.id) selectHomeCharacter(id);
  audio.stopVoice(); audio.playUi(); newGame(`迎戰${difficulty.name}`); enterMatch();
}

function openCharacters(): void { setModal(rulesModal, false); setModal(characterPicker, true); query<HTMLButtonElement>('#closePickerBtn').focus(); }
function openRules(): void { setModal(characterPicker, false); setModal(rulesModal, true); query<HTMLButtonElement>('#closeRulesBtn').focus(); }
function setVoiceStudioStatus(message: string, activeKey = ''): void {
  query<HTMLElement>('#voiceStudioStatus').textContent = message;
  document.querySelectorAll<HTMLElement>('[data-voice-row]').forEach((row) => row.classList.toggle('is-playing', row.dataset.voiceRow === activeKey));
  document.querySelectorAll<HTMLElement>('[data-native-row]').forEach((row) => row.classList.toggle('is-playing', row.dataset.nativeRow === activeKey));
}

function stopVoiceAudition(message = '已停止播放。'): void {
  voiceAuditionRun += 1;
  audio.stopVoice();
  setVoiceStudioStatus(message);
}

async function auditionVoice(characterId: string, cue: VoiceCue, run = ++voiceAuditionRun): Promise<void> {
  const character = difficultyById(characterId);
  if (!character || !VOICE_LINES[characterId]?.[cue]) return;
  audio.enableVoice();
  renderAudioControls();
  setVoiceStudioStatus(`正在播放：${character.name}・${VOICE_CUE_LABEL[cue]}`, `${characterId}-${cue}`);
  await audio.playVoice(characterId, cue);
  if (run === voiceAuditionRun) setVoiceStudioStatus(`已播放：${character.name}・${VOICE_CUE_LABEL[cue]}`);
}

async function auditionNativeVoice(characterId: string): Promise<void> {
  const run = ++voiceAuditionRun;
  const character = difficultyById(characterId);
  if (!character) return;
  audio.enableVoice(); renderAudioControls();
  setVoiceStudioStatus(`正在播放角色聲線：${character.name}・${VOICE_CAST[characterId].label}`, characterId);
  await audio.playVoicePreview(characterId);
  if (run === voiceAuditionRun) setVoiceStudioStatus(`已播放新聲線：${character.name}`);
}

async function auditionAllVoices(): Promise<void> {
  const run = ++voiceAuditionRun;
  audio.enableVoice();
  renderAudioControls();
  for (const character of DIFFICULTIES) {
    for (const cue of VOICE_CUES) {
      if (run !== voiceAuditionRun) return;
      await auditionVoice(character.id, cue, run);
    }
  }
  if (run === voiceAuditionRun) setVoiceStudioStatus('全部 20 段配音已播放完畢。');
}

function openVoiceStudio(): void {
  voiceStudioReturnToPicker = characterPicker.classList.contains('is-open');
  setModal(characterPicker, false); setModal(rulesModal, false); setModal(trainingModal, false); setModal(resultDialog, false);
  setModal(voiceStudioModal, true); setVoiceStudioStatus('請先點選綠色區塊的「新聲線試音」。'); query<HTMLButtonElement>('#closeVoiceStudioBtn').focus();
}

function closeVoiceStudio(): void {
  stopVoiceAudition(); setModal(voiceStudioModal, false);
  if (voiceStudioReturnToPicker) { voiceStudioReturnToPicker = false; setModal(characterPicker, true); }
}

function openTraining(): void {
  setModal(characterPicker, false); setModal(rulesModal, false); setModal(resultDialog, false);
  selectedPuzzle = dailyPuzzle(); trainingSolved = false; trainingMessage = ''; renderTraining();
  setModal(trainingModal, true); query<HTMLButtonElement>('#closeTrainingBtn').focus();
}

function startReplay(): void {
  if (!replayMoves.length) return;
  setModal(resultDialog, false); replayMode = true; replayIndex = replayMoves.length; render();
  window.requestAnimationFrame(() => query<HTMLElement>('.board-column').scrollIntoView({ block: 'start' }));
}

function stepReplay(delta: number): void {
  replayIndex = Math.max(0, Math.min(replayMoves.length, replayIndex + delta));
  audio.playUi(); render();
}

async function undoLastRound(): Promise<void> {
  if (thinking || !moves.length) return;
  audio.playUi(); window.clearTimeout(resultTimer); setModal(resultDialog, false);
  const wasGameOver = gameOver;
  if (profileBeforeResult) { profile = profileBeforeResult; profileBeforeResult = null; saveProfile(profile); }
  const remove = moves.at(-1)?.side === humanSide ? 1 : Math.min(2, moves.length);
  moves = moves.slice(0, -remove); gameOver = false; winning = new Set(); moveInsight = null; replayMode = false; replayMoves = []; replayIndex = 0;
  await refreshForbidden(); saveGame(); render();
  toast(wasGameOver ? '已悔回勝負手，對局重新進行' : '已退回上一回合');
  window.setTimeout(() => audio.playVoice(difficulty.id, 'undo'), 180);
}

boardGrid.addEventListener('click', (event) => {
  const cell = (event.target as HTMLElement).closest<HTMLButtonElement>('.intersection'); if (!cell) return;
  if (event.detail > 0) cell.blur();
  void humanMove(Number(cell.dataset.x), Number(cell.dataset.y));
});
query<HTMLDivElement>('#pickerGrid').addEventListener('click', (event) => {
  const card = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-pick]'); if (card) chooseCharacter(card.dataset.pick ?? '', true);
});
query<HTMLDivElement>('#trainingModal').addEventListener('click', (event) => {
  const target = event.target as HTMLElement;
  const puzzleButton = target.closest<HTMLButtonElement>('[data-training-id]');
  if (puzzleButton) {
    selectedPuzzle = TRAINING_PUZZLES.find((puzzle) => puzzle.id === puzzleButton.dataset.trainingId) ?? selectedPuzzle;
    trainingSolved = false; trainingMessage = ''; audio.playUi(); renderTraining(); return;
  }
  if (target.closest('#trainingHintBtn')) { trainingMessage = `提示：${selectedPuzzle.hint}`; audio.playUi(); renderTraining(); return; }
  const cell = target.closest<HTMLButtonElement>('[data-training-x]');
  if (!cell || trainingSolved) return;
  const x = Number(cell.dataset.trainingX); const y = Number(cell.dataset.trainingY);
  if (!isPuzzleSolution(selectedPuzzle, x, y)) {
    trainingMessage = '這手還沒有抓住勝負核心。再看一次對手的下一手。'; audio.playUi(); renderTraining(); return;
  }
  trainingSolved = true; trainingMessage = selectedPuzzle.success;
  const today = localDateKey(); const dailyKey = selectedPuzzle.id === dailyPuzzle().id ? today : undefined;
  profile = recordPuzzle(profile, selectedPuzzle.id, dailyKey); saveProfile(profile); audio.playOutcome(true); renderTraining(); renderProfile();
});

ruleSelect.addEventListener('change', () => { rule = ruleSelect.value as Rule; audio.playUi(); newGame(`已切換為${RULE_LABEL[rule]}`); });
sideSelect.addEventListener('change', () => { humanSide = Number(sideSelect.value) === 2 ? 2 : 1; audio.playUi(); newGame(humanSide === 1 ? '你執黑先行' : '你執白，由守局人先行'); });
newGameBtn.addEventListener('click', () => { audio.playUi(); newGame(); });
swapBtn.addEventListener('click', () => { audio.playUi(); humanSide = humanSide === 1 ? 2 : 1; sideSelect.value = String(humanSide); newGame('已交換棋色'); });
undoBtn.addEventListener('click', async () => {
  await undoLastRound();
});

query<HTMLElement>('#coverCast').addEventListener('click', (event) => {
  if (!document.body.classList.contains('home-active')) return;
  const role = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-home-role]');
  if (role) selectHomeCharacter(role.dataset.homeRole ?? '');
});
query<HTMLElement>('#coverCast').addEventListener('dblclick', (event) => {
  const role = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-home-role]');
  if (!role || role.dataset.homeRole !== difficulty.id) return;
  event.preventDefault(); startHomeMatch(role.dataset.homeRole);
});
query<HTMLElement>('#coverCast').addEventListener('pointerup', (event) => {
  if (event.pointerType !== 'touch') return;
  const role = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-home-role]');
  if (!role) return;
  const roleId = role.dataset.homeRole ?? '';
  const now = performance.now();
  if (roleId === lastHomeTouchRole && now - lastHomeTouchAt < 700) {
    event.preventDefault(); lastHomeTouchRole = ''; lastHomeTouchAt = 0; startHomeMatch(roleId); return;
  }
  lastHomeTouchRole = roleId; lastHomeTouchAt = now;
});
query<HTMLElement>('#coverCast').addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const role = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-home-role]');
  if (!role) return;
  event.preventDefault();
  const roleId = role.dataset.homeRole ?? '';
  if (roleId === difficulty.id) startHomeMatch(roleId); else selectHomeCharacter(roleId);
});
query<HTMLButtonElement>('#expandPickerBtn').addEventListener('click', openCharacters);
query<HTMLButtonElement>('#homeBtn').addEventListener('click', goHome);
query<HTMLButtonElement>('#returnHomeBtn').addEventListener('click', goHome);
query<HTMLButtonElement>('#closePickerBtn').addEventListener('click', () => setModal(characterPicker, false));
query<HTMLButtonElement>('#homeRulesBtn').addEventListener('click', openRules);
query<HTMLButtonElement>('#rulesLinkBtn').addEventListener('click', openRules);
query<HTMLButtonElement>('#homeVoiceStudioBtn').addEventListener('click', openVoiceStudio);
query<HTMLButtonElement>('#voiceStudioLinkBtn').addEventListener('click', openVoiceStudio);
query<HTMLButtonElement>('#pickerVoiceStudioBtn').addEventListener('click', openVoiceStudio);
query<HTMLButtonElement>('#homeTrainingBtn').addEventListener('click', openTraining);
query<HTMLButtonElement>('#trainingLinkBtn').addEventListener('click', openTraining);
query<HTMLButtonElement>('#closeRulesBtn').addEventListener('click', () => setModal(rulesModal, false));
query<HTMLButtonElement>('#closeVoiceStudioBtn').addEventListener('click', closeVoiceStudio);
query<HTMLButtonElement>('#playAllVoicesBtn').addEventListener('click', () => { void auditionAllVoices(); });
query<HTMLButtonElement>('#stopVoicesBtn').addEventListener('click', () => stopVoiceAudition());
voiceStudioModal.addEventListener('click', (event) => {
  const nativeButton = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-native-preview]');
  if (nativeButton) { void auditionNativeVoice(nativeButton.dataset.nativePreview ?? ''); return; }
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-voice-character]');
  if (!button) return;
  const cue = button.dataset.voiceCue as VoiceCue;
  if (!VOICE_CUES.includes(cue)) return;
  void auditionVoice(button.dataset.voiceCharacter ?? '', cue);
});
query<HTMLButtonElement>('#closeTrainingBtn').addEventListener('click', () => setModal(trainingModal, false));
query<HTMLButtonElement>('#resultRematchBtn').addEventListener('click', () => { audio.playUi(); newGame('再戰一局'); });
query<HTMLButtonElement>('#resultUndoBtn').addEventListener('click', () => { void undoLastRound(); });
query<HTMLButtonElement>('#resultCharacterBtn').addEventListener('click', () => { setModal(resultDialog, false); openCharacters(); });
query<HTMLButtonElement>('#resultBoardBtn').addEventListener('click', startReplay);
query<HTMLButtonElement>('#replayPrevBtn').addEventListener('click', () => stepReplay(-1));
query<HTMLButtonElement>('#replayNextBtn').addEventListener('click', () => stepReplay(1));
query<HTMLButtonElement>('#replayExitBtn').addEventListener('click', () => { replayMode = false; render(); });
document.querySelectorAll<HTMLElement>('.modal-backdrop').forEach((backdrop) => backdrop.addEventListener('click', () => {
  const modal = backdrop.parentElement!; setModal(modal, false);
  if (modal === voiceStudioModal) { stopVoiceAudition(); if (voiceStudioReturnToPicker) { voiceStudioReturnToPicker = false; setModal(characterPicker, true); } }
}));
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  if (resultDialog.classList.contains('is-open')) setModal(resultDialog, false);
  else if (trainingModal.classList.contains('is-open')) setModal(trainingModal, false);
  else if (voiceStudioModal.classList.contains('is-open')) closeVoiceStudio();
  else if (rulesModal.classList.contains('is-open')) setModal(rulesModal, false);
  else if (characterPicker.classList.contains('is-open')) setModal(characterPicker, false);
});

(['music', 'sound', 'voice'] as const).forEach((kind) => {
  query<HTMLButtonElement>(`#${kind}Btn`).addEventListener('click', async () => {
    await audio.toggle(kind); renderAudioControls();
    toast(`${kind === 'music' ? '背景音樂' : kind === 'sound' ? '遊戲音效' : '角色配音'}已${audio.prefs[kind] ? '開啟' : '關閉'}`);
  });
});

query<HTMLButtonElement>('#fullscreenBtn').addEventListener('click', async () => {
  try { if (document.fullscreenElement) await document.exitFullscreen(); else await document.documentElement.requestFullscreen(); }
  catch { toast('此瀏覽器不支援全螢幕模式'); }
  renderAudioControls();
});
document.addEventListener('fullscreenchange', renderAudioControls);
document.addEventListener('pointerdown', () => { void audio.unlock(); }, { once: true });

loadGame(); render(); document.body.classList.add('home-active');
